/* Heirloom Tenant Layout — album-cover, hand-pressed, warm.

   What makes this different from Editorial:
   • Wrapped in a subtle paper-cream frame border on desktop — the whole
     site sits inside a "print" the way a photo sits inside a mat.
   • Header reads like the embossed cover of a family album: thick caramel
     ribbon band across the top with a centered serif wordmark and a small
     "Volume · I" stamp on the right.
   • Nav appears beneath the ribbon as small caps with caramel separator
     dots between items (·) — feels typeset, not coded.
   • Footer is an "album credits page" — STUDIO / CONTACT / FOLLOW columns
     each separated by a vertical caramel rule, with a "Volume I · Year"
     spine line below.
*/

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";
import { Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/",          label: "Home",      end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services",  label: "Services"  },
  { to: "/contact",   label: "Contact"   },
];

export default function HeirloomTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = new Date().getFullYear();
  // Per-template stamps (Heirloom's album spine inscription) — editable
  // in Site Settings → Template-specific so a studio can use "Volume IV"
  // or "Edition 02" instead of the default "Volume · I".
  const tf = settings?.template_fields?.heirloom || {};
  const volumeText = (tf.volume || "I").toString();
  const yearText = (tf.year || String(year)).toString();

  return (
    <div
      className="min-h-screen antialiased"
      style={{
        fontFamily: "var(--tenant-sans)",
        background: "var(--tenant-bg, #F8F2EA)",
        color: "var(--tenant-fg, #2E2419)",
      }}
      data-template="heirloom"
    >
      {/* ── Album-ribbon masthead ─────────────────────────────────────── */}
      <header
        className="fixed top-0 inset-x-0 z-40"
        style={{
          background: "var(--tenant-bg, #F8F2EA)",
          borderBottom: scrolled ? "1px solid var(--tenant-fg-line)" : "none",
        }}
        data-testid="heirloom-tenant-nav"
      >
        {/* Mobile-only hamburger — pinned over the album ribbon (top-right
            of the 3-column band). The ribbon is light-on-caramel, so we
            use tone="light" for the icon. */}
        <div className="absolute right-3 top-2 md:hidden">
          <TenantMobileNav tone="light" ctaLabel="Book a sitting" studioName={studioName} />
        </div>
        {/* Caramel ribbon band */}
        <div
          className="grid grid-cols-3 items-center h-12 lg:h-14 px-6 lg:px-12"
          style={{
            background: "var(--tenant-accent)",
            color: "color-mix(in srgb, var(--tenant-bg, #F8F2EA) 95%, white)",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.32em] opacity-80">
            Volume · {volumeText}
          </div>
          <Link
            to={tenantHref("/")}
            className="text-center text-base lg:text-lg italic"
            style={{ fontFamily: "var(--tenant-serif)" }}
            data-testid="heirloom-tenant-wordmark"
          >
            <TenantWordmark logoUrl={settings?.logo_url} coverLogoUrl={settings?.cover_logo_url} tone="dark" studioName={studioName} imgClassName="h-8 lg:h-10 w-auto mx-auto">
              {studioName}
            </TenantWordmark>
          </Link>
          <div className="text-right text-[10px] uppercase tracking-[0.32em] opacity-80">
            {yearText}
          </div>
        </div>
        {/* Nav strip below the ribbon */}
        <nav className="hidden md:flex justify-center items-center gap-1 h-10 text-[10px] uppercase tracking-[0.32em]" aria-label="Primary">
          {NAV.map((it, i) => (
            <span key={it.to} className="flex items-center gap-1">
              <NavLink
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`heirloom-tenant-nav-${it.label.toLowerCase()}`}
                className="px-3 py-1 transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? "var(--tenant-accent)" : "var(--tenant-fg-soft, rgba(46,36,25,0.55))",
                })}
              >
                {it.label}
              </NavLink>
              {i < NAV.length - 1 && (
                <span style={{ color: "var(--tenant-accent)", opacity: 0.5 }}>·</span>
              )}
            </span>
          ))}
          <span style={{ color: "var(--tenant-accent)", opacity: 0.5, marginLeft: 4 }}>·</span>
          <Link
            to={tenantHref("/booking")}
            className="px-3 py-1 transition-colors hover:opacity-100"
            style={{ color: "var(--tenant-accent)", opacity: 0.95 }}
            data-testid="heirloom-tenant-book-cta"
          >
            Book a Sitting
          </Link>
        </nav>
      </header>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <main key={location.pathname} className="pt-24 lg:pt-28">
        <Outlet />
      </main>

      {/* ── Album-credits footer ──────────────────────────────────────── */}
      <footer
        className="border-t mt-16"
        style={{
          background: "var(--tenant-bg, #F8F2EA)",
          color: "var(--tenant-fg, #2E2419)",
          borderTopColor: "var(--tenant-accent)",
        }}
        data-testid="heirloom-tenant-footer"
      >
        {/* Wordmark band */}
        <div className="text-center pt-10 pb-6">
          <div className="text-3xl italic" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-accent)" }}>
            {studioName}
          </div>
          {settings?.tagline && (
            <p className="text-sm italic mt-2 max-w-md mx-auto leading-relaxed" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
              {settings.tagline}
            </p>
          )}
        </div>
        {/* 3-column credits */}
        <div className="grid grid-cols-1 md:grid-cols-3 max-w-[1280px] mx-auto px-6 lg:px-12 text-center text-sm" style={{ color: "var(--tenant-fg-soft)" }}>
          {[
            { label: "Studio", value: settings?.contact_location ? <span className="italic" style={{ fontFamily: "var(--tenant-serif)" }}>{settings.contact_location}</span> : studioName },
            { label: "Contact", value: settings?.contact_email ? <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.85 }}><Mail className="w-3.5 h-3.5" strokeWidth={1.25} /> {settings.contact_email}</a> : null },
            { label: "Follow", value: settings?.instagram_url ? <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center hover:opacity-100" style={{ opacity: 0.85 }}><Instagram className="w-4 h-4" strokeWidth={1.25} /></a> : null },
          ].map((c, i) => (
            <div
              key={c.label}
              className="py-6 md:py-8 md:px-6"
              style={{
                borderRight: i < 2 ? "1px solid var(--tenant-accent)" : "none",
                borderRightStyle: "dotted",
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>
                {c.label}
              </div>
              {c.value || <span className="opacity-50">—</span>}
            </div>
          ))}
        </div>
        {/* Spine line */}
        <div className="text-center text-[10px] uppercase tracking-[0.42em] pt-8 pb-6 mt-4" style={{ color: "var(--tenant-fg-mute)", borderTop: "1px dotted var(--tenant-accent)" }}>
          <span>© {year} {studioName}</span>
          <span className="mx-3 opacity-50">·</span>
          <span style={{ color: "var(--tenant-accent)" }}>Volume {volumeText}</span>
          <span className="mx-3 opacity-50">·</span>
          <LumetierFooterCredit className="hover:opacity-100" testid="heirloom-lumetier-credit" />
        </div>
      </footer>

      <TenantSignature tone="light" />
    </div>
  );
}
